/**
 * Directory Sync Service
 * 
 * Provides three industry-standard methods for importing organizational structure:
 * 1. SCIM 2.0 - Modern cloud standard (Okta, Azure AD, Google Workspace)
 * 2. LDAP - On-premises Active Directory environments
 * 3. CSV Import - Manual fallback when integrations aren't available
 * 
 * This service handles parsing, validation, and transformation of org chart data
 * from various sources into our internal OrgUnit format.
 */

import type { OrgUnit, OrgUnitType } from '../types/prioritization/enterpriseIdentity';

// ============================================================================
// CSV Import Types & Parser
// ============================================================================

/**
 * Expected CSV column headers for org unit import
 */
export interface CsvOrgUnitRow {
  id?: string;                    // External ID (optional, will generate if missing)
  name: string;                   // Internal name (slug-like)
  displayName: string;            // Human-readable name
  type: string;                   // organization, division, department, team, workgroup, project
  parentId?: string;              // Parent org unit ID (empty for root)
  parentName?: string;            // Alternative: parent name (if ID not available)
  description?: string;           // Optional description
  costCenter?: string;            // Optional cost center
  location?: string;              // Optional location
  active?: string;                // "true" or "false" (default: true)
  managerId?: string;             // Optional manager user ID
  managerEmail?: string;          // Alternative: manager email
}

/**
 * Expected CSV column headers for user-to-org-unit membership import
 */
export interface CsvMembershipRow {
  userId?: string;                // User ID
  userEmail: string;              // User email (primary identifier)
  orgUnitId?: string;             // Org unit ID
  orgUnitName?: string;           // Alternative: org unit name
  membershipType?: string;        // member, manager, lead, admin (default: member)
  isPrimary?: string;             // "true" or "false" (default: false)
}

/**
 * Result of CSV parsing operation
 */
export interface CsvParseResult<T> {
  success: boolean;
  data: T[];
  errors: CsvParseError[];
  warnings: string[];
  stats: {
    totalRows: number;
    validRows: number;
    skippedRows: number;
  };
}

export interface CsvParseError {
  row: number;
  column?: string;
  message: string;
  value?: string;
}

/**
 * Parse CSV text into org unit records
 */
export function parseOrgUnitCsv(csvText: string): CsvParseResult<CsvOrgUnitRow> {
  const errors: CsvParseError[] = [];
  const warnings: string[] = [];
  const data: CsvOrgUnitRow[] = [];
  
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) {
    return {
      success: false,
      data: [],
      errors: [{ row: 0, message: 'CSV must have a header row and at least one data row' }],
      warnings: [],
      stats: { totalRows: 0, validRows: 0, skippedRows: 0 },
    };
  }
  
  // Parse header row
  const headers = parseCsvLine(lines[0]).map(h => h.toLowerCase().trim());
  const requiredHeaders = ['name', 'displayname', 'type'];
  const missingHeaders = requiredHeaders.filter(h => 
    !headers.includes(h) && !headers.includes(h.replace(/([A-Z])/g, '_$1').toLowerCase())
  );
  
  if (missingHeaders.length > 0) {
    // Try alternate header names
    const alternates: Record<string, string[]> = {
      'name': ['name', 'unit_name', 'org_name'],
      'displayname': ['displayname', 'display_name', 'title', 'label'],
      'type': ['type', 'unit_type', 'org_type', 'level'],
    };
    
    const stillMissing = missingHeaders.filter(h => {
      const alts = alternates[h] || [];
      return !alts.some(alt => headers.includes(alt));
    });
    
    if (stillMissing.length > 0) {
      return {
        success: false,
        data: [],
        errors: [{ row: 0, message: `Missing required headers: ${stillMissing.join(', ')}` }],
        warnings: [],
        stats: { totalRows: lines.length - 1, validRows: 0, skippedRows: lines.length - 1 },
      };
    }
  }
  
  // Map header indices
  const headerMap: Record<string, number> = {};
  headers.forEach((h, i) => {
    // Normalize header names
    const normalized = h.replace(/_/g, '').toLowerCase();
    headerMap[normalized] = i;
  });
  
  const getCol = (row: string[], ...names: string[]): string | undefined => {
    for (const name of names) {
      const normalized = name.replace(/_/g, '').toLowerCase();
      if (headerMap[normalized] !== undefined) {
        return row[headerMap[normalized]]?.trim();
      }
    }
    return undefined;
  };
  
  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const row = parseCsvLine(line);
    
    try {
      const name = getCol(row, 'name', 'unit_name', 'org_name');
      const displayName = getCol(row, 'displayName', 'display_name', 'title', 'label');
      const type = getCol(row, 'type', 'unit_type', 'org_type', 'level');
      
      if (!name || !displayName || !type) {
        errors.push({
          row: i + 1,
          message: 'Missing required field (name, displayName, or type)',
        });
        continue;
      }
      
      // Validate type
      const validTypes: OrgUnitType[] = ['organization', 'division', 'department', 'team', 'workgroup', 'project'];
      const normalizedType = type.toLowerCase() as OrgUnitType;
      if (!validTypes.includes(normalizedType)) {
        errors.push({
          row: i + 1,
          column: 'type',
          message: `Invalid type "${type}". Must be one of: ${validTypes.join(', ')}`,
          value: type,
        });
        continue;
      }
      
      data.push({
        id: getCol(row, 'id', 'external_id', 'org_id'),
        name,
        displayName,
        type: normalizedType,
        parentId: getCol(row, 'parentId', 'parent_id'),
        parentName: getCol(row, 'parentName', 'parent_name', 'parent'),
        description: getCol(row, 'description', 'desc'),
        costCenter: getCol(row, 'costCenter', 'cost_center'),
        location: getCol(row, 'location', 'office'),
        active: getCol(row, 'active', 'enabled', 'status'),
        managerId: getCol(row, 'managerId', 'manager_id'),
        managerEmail: getCol(row, 'managerEmail', 'manager_email', 'manager'),
      });
    } catch (error) {
      errors.push({
        row: i + 1,
        message: `Parse error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }
  }
  
  return {
    success: errors.length === 0,
    data,
    errors,
    warnings,
    stats: {
      totalRows: lines.length - 1,
      validRows: data.length,
      skippedRows: lines.length - 1 - data.length,
    },
  };
}

/**
 * Parse a single CSV line handling quoted values
 */
function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);
  
  return result;
}

/**
 * Convert parsed CSV rows to OrgUnit objects with proper hierarchy
 */
export function csvRowsToOrgUnits(
  rows: CsvOrgUnitRow[],
  existingUnits: Map<string, OrgUnit> = new Map()
): { units: OrgUnit[]; errors: string[] } {
  const errors: string[] = [];
  const units: OrgUnit[] = [];
  const nameToIdMap = new Map<string, string>();
  
  // First pass: create units and build name-to-id map
  rows.forEach((row, index) => {
    const id = row.id || `org-import-${Date.now()}-${index}`;
    nameToIdMap.set(row.name.toLowerCase(), id);
    
    // Try to resolve parent
    let parentId = row.parentId;
    if (!parentId && row.parentName) {
      parentId = nameToIdMap.get(row.parentName.toLowerCase());
      if (!parentId) {
        // Check existing units
        for (const [existingId, existingUnit] of existingUnits) {
          if (existingUnit.name.toLowerCase() === row.parentName.toLowerCase() ||
              existingUnit.displayName.toLowerCase() === row.parentName.toLowerCase()) {
            parentId = existingId;
            break;
          }
        }
      }
    }
    
    // Build path
    const path: string[] = [];
    if (parentId) {
      const parent = existingUnits.get(parentId) || units.find(u => u.id === parentId);
      if (parent) {
        path.push(...parent.path);
      }
    }
    path.push(id);
    
    const unit: OrgUnit = {
      id,
      externalId: row.id,
      name: row.name,
      displayName: row.displayName,
      type: row.type as OrgUnitType,
      parentId,
      path,
      depth: path.length - 1,
      description: row.description,
      costCenter: row.costCenter,
      location: row.location,
      syncSource: 'local',
      active: row.active?.toLowerCase() !== 'false',
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    
    units.push(unit);
  });
  
  // Second pass: fix paths for any units whose parents were defined later in the CSV
  units.forEach(unit => {
    if (unit.parentId && unit.path.length === 1) {
      const parent = units.find(u => u.id === unit.parentId);
      if (parent) {
        unit.path = [...parent.path, unit.id];
        unit.depth = unit.path.length - 1;
      }
    }
  });
  
  return { units, errors };
}

/**
 * Generate a sample CSV template for org units
 */
export function generateOrgUnitCsvTemplate(): string {
  return `name,displayName,type,parentName,description,costCenter,location,active
acme,Acme Corporation,organization,,"The parent company",,Headquarters,true
engineering,Engineering,division,acme,"Engineering division",ENG-001,Building A,true
product-dev,Product Development,department,engineering,"Product development team",ENG-002,Building A,true
frontend-team,Frontend Team,team,product-dev,"Web frontend development",,Building A,true
backend-team,Backend Team,team,product-dev,"API and services development",,Building A,true
devops,DevOps,team,engineering,"Infrastructure and deployment",,Building B,true
sales,Sales,division,acme,"Sales organization",SALES-001,Building C,true
enterprise-sales,Enterprise Sales,department,sales,"Enterprise accounts",,Building C,true
smb-sales,SMB Sales,department,sales,"Small and medium business",,Building C,true
`;
}

// ============================================================================
// SCIM 2.0 Types & Integration
// ============================================================================

/**
 * SCIM 2.0 User Resource Schema
 * @see https://datatracker.ietf.org/doc/html/rfc7643#section-4.1
 */
export interface ScimUser {
  schemas: string[];
  id: string;
  externalId?: string;
  userName: string;
  name?: {
    formatted?: string;
    familyName?: string;
    givenName?: string;
    middleName?: string;
    honorificPrefix?: string;
    honorificSuffix?: string;
  };
  displayName?: string;
  nickName?: string;
  profileUrl?: string;
  title?: string;
  userType?: string;
  preferredLanguage?: string;
  locale?: string;
  timezone?: string;
  active?: boolean;
  emails?: Array<{
    value: string;
    type?: string;
    primary?: boolean;
  }>;
  phoneNumbers?: Array<{
    value: string;
    type?: string;
  }>;
  photos?: Array<{
    value: string;
    type?: string;
  }>;
  groups?: Array<{
    value: string;  // Group ID
    $ref?: string;
    display?: string;
  }>;
  // Enterprise extension for manager/department
  'urn:ietf:params:scim:schemas:extension:enterprise:2.0:User'?: {
    employeeNumber?: string;
    costCenter?: string;
    organization?: string;
    division?: string;
    department?: string;
    manager?: {
      value?: string;      // Manager's user ID
      $ref?: string;
      displayName?: string;
    };
  };
  meta?: {
    resourceType?: string;
    created?: string;
    lastModified?: string;
    location?: string;
    version?: string;
  };
}

/**
 * SCIM 2.0 Group Resource Schema
 * @see https://datatracker.ietf.org/doc/html/rfc7643#section-4.2
 */
export interface ScimGroup {
  schemas: string[];
  id: string;
  externalId?: string;
  displayName: string;
  members?: Array<{
    value: string;  // User ID
    $ref?: string;
    display?: string;
    type?: 'User' | 'Group';
  }>;
  meta?: {
    resourceType?: string;
    created?: string;
    lastModified?: string;
    location?: string;
    version?: string;
  };
}

/**
 * SCIM Configuration for endpoint setup
 */
export interface ScimConfig {
  enabled: boolean;
  baseUrl: string;              // Our SCIM endpoint base URL
  bearerToken?: string;         // Token for authenticating incoming requests
  
  // Attribute mappings
  userMapping: {
    departmentAttribute: string;  // Where to find department (e.g., 'department' or enterprise extension)
    managerAttribute: string;     // Where to find manager
    groupsAsOrgUnits: boolean;    // Should SCIM groups become org units?
  };
  
  // Behavior settings
  autoCreateOrgUnits: boolean;    // Create org units from department attribute
  autoAssignMembership: boolean;  // Auto-assign users to org units based on department
  deactivateOnRemove: boolean;    // Deactivate rather than delete on SCIM DELETE
}

/**
 * Convert SCIM groups to OrgUnits
 * Used when syncing from identity providers that expose groups via SCIM
 */
export function scimGroupsToOrgUnits(
  groups: ScimGroup[],
  config: { 
    defaultType: OrgUnitType; 
    parentId?: string;
    typeMapping?: Record<string, OrgUnitType>;
  }
): OrgUnit[] {
  return groups.map((group) => {
    // Try to determine type from group name patterns
    let type = config.defaultType;
    if (config.typeMapping) {
      for (const [pattern, mappedType] of Object.entries(config.typeMapping)) {
        if (group.displayName.toLowerCase().includes(pattern.toLowerCase())) {
          type = mappedType;
          break;
        }
      }
    }
    
    const id = `org-scim-${group.id}`;
    const parentPath = config.parentId ? [config.parentId] : [];
    
    return {
      id,
      externalId: group.id,
      name: group.displayName.toLowerCase().replace(/\s+/g, '-'),
      displayName: group.displayName,
      type,
      parentId: config.parentId,
      path: [...parentPath, id],
      depth: parentPath.length,
      syncSource: 'okta', // or determined from provider
      lastSyncedAt: new Date(),
      active: true,
      createdAt: group.meta?.created ? new Date(group.meta.created) : new Date(),
      updatedAt: group.meta?.lastModified ? new Date(group.meta.lastModified) : new Date(),
    };
  });
}

/**
 * Extract department hierarchy from SCIM users' enterprise extension
 */
export function extractDepartmentsFromScimUsers(users: ScimUser[]): Map<string, Set<string>> {
  // Map of department name -> set of parent departments (for hierarchy detection)
  const departments = new Map<string, Set<string>>();
  
  users.forEach(user => {
    const enterprise = user['urn:ietf:params:scim:schemas:extension:enterprise:2.0:User'];
    if (!enterprise) return;
    
    const { organization, division, department } = enterprise;
    
    // Build hierarchy: organization -> division -> department
    if (organization && !departments.has(organization)) {
      departments.set(organization, new Set());
    }
    
    if (division) {
      if (!departments.has(division)) {
        departments.set(division, new Set());
      }
      if (organization) {
        departments.get(division)!.add(organization);
      }
    }
    
    if (department) {
      if (!departments.has(department)) {
        departments.set(department, new Set());
      }
      if (division) {
        departments.get(department)!.add(division);
      } else if (organization) {
        departments.get(department)!.add(organization);
      }
    }
  });
  
  return departments;
}

// ============================================================================
// LDAP Types & Integration
// ============================================================================

/**
 * LDAP Connection Configuration
 */
export interface LdapConfig {
  enabled: boolean;
  
  // Connection settings
  url: string;                    // e.g., ldap://ad.company.com:389 or ldaps://...
  baseDn: string;                 // e.g., dc=company,dc=com
  bindDn: string;                 // e.g., cn=admin,dc=company,dc=com
  bindCredential?: string;        // Password (stored securely)
  
  // TLS settings
  useTls: boolean;
  tlsCertificate?: string;
  validateCertificate: boolean;
  
  // Search settings
  userSearchBase: string;         // e.g., ou=Users,dc=company,dc=com
  userSearchFilter: string;       // e.g., (objectClass=person)
  groupSearchBase: string;        // e.g., ou=Groups,dc=company,dc=com
  groupSearchFilter: string;      // e.g., (objectClass=group)
  ouSearchBase: string;           // e.g., dc=company,dc=com
  ouSearchFilter: string;         // e.g., (objectClass=organizationalUnit)
  
  // Attribute mappings
  attributeMapping: {
    // User attributes
    userId: string;               // e.g., sAMAccountName, uid
    email: string;                // e.g., mail, userPrincipalName
    displayName: string;          // e.g., displayName, cn
    firstName: string;            // e.g., givenName
    lastName: string;             // e.g., sn
    department: string;           // e.g., department
    title: string;                // e.g., title
    manager: string;              // e.g., manager (DN reference)
    memberOf: string;             // e.g., memberOf (group memberships)
    
    // Group/OU attributes
    groupName: string;            // e.g., cn, name
    groupDescription: string;     // e.g., description
    groupMembers: string;         // e.g., member
    ouName: string;               // e.g., ou, name
  };
  
  // Sync settings
  syncInterval: number;           // Minutes between syncs
  pageSize: number;               // LDAP page size for large directories
  
  // Behavior
  groupsAsOrgUnits: boolean;      // Treat LDAP groups as org units
  ousAsOrgUnits: boolean;         // Treat LDAP OUs as org units
  autoCreateUsers: boolean;       // Create users in our system
}

/**
 * Default LDAP configuration for Active Directory
 */
export const DEFAULT_AD_LDAP_CONFIG: Partial<LdapConfig> = {
  useTls: false,
  validateCertificate: true,
  userSearchFilter: '(&(objectClass=user)(objectCategory=person))',
  groupSearchFilter: '(objectClass=group)',
  ouSearchFilter: '(objectClass=organizationalUnit)',
  attributeMapping: {
    userId: 'sAMAccountName',
    email: 'userPrincipalName',
    displayName: 'displayName',
    firstName: 'givenName',
    lastName: 'sn',
    department: 'department',
    title: 'title',
    manager: 'manager',
    memberOf: 'memberOf',
    groupName: 'cn',
    groupDescription: 'description',
    groupMembers: 'member',
    ouName: 'ou',
  },
  syncInterval: 60,
  pageSize: 1000,
  groupsAsOrgUnits: true,
  ousAsOrgUnits: true,
  autoCreateUsers: true,
};

/**
 * Default LDAP configuration for OpenLDAP
 */
export const DEFAULT_OPENLDAP_CONFIG: Partial<LdapConfig> = {
  useTls: false,
  validateCertificate: true,
  userSearchFilter: '(objectClass=inetOrgPerson)',
  groupSearchFilter: '(objectClass=groupOfNames)',
  ouSearchFilter: '(objectClass=organizationalUnit)',
  attributeMapping: {
    userId: 'uid',
    email: 'mail',
    displayName: 'cn',
    firstName: 'givenName',
    lastName: 'sn',
    department: 'ou',
    title: 'title',
    manager: 'manager',
    memberOf: 'memberOf',
    groupName: 'cn',
    groupDescription: 'description',
    groupMembers: 'member',
    ouName: 'ou',
  },
  syncInterval: 60,
  pageSize: 1000,
  groupsAsOrgUnits: true,
  ousAsOrgUnits: true,
  autoCreateUsers: true,
};

/**
 * Simulated LDAP search result (for UI preview/testing)
 * In production, this would be replaced with actual LDAP client calls
 */
export interface LdapSearchResult {
  dn: string;
  attributes: Record<string, string | string[]>;
}

/**
 * Convert LDAP organizational units to our OrgUnit format
 */
export function ldapOUsToOrgUnits(
  ldapOUs: LdapSearchResult[],
  config: LdapConfig,
  baseDnParts: string[]
): OrgUnit[] {
  const units: OrgUnit[] = [];
  const dnToIdMap = new Map<string, string>();
  
  // Sort by DN depth (shorter DNs first = higher in hierarchy)
  const sorted = [...ldapOUs].sort((a, b) => {
    const aDepth = a.dn.split(',').length;
    const bDepth = b.dn.split(',').length;
    return aDepth - bDepth;
  });
  
  sorted.forEach((ou, index) => {
    const dnParts = ou.dn.split(',').map(p => p.trim());
    const ouName = ou.attributes[config.attributeMapping.ouName];
    const name = Array.isArray(ouName) ? ouName[0] : ouName;
    
    if (!name) return;
    
    const id = `org-ldap-${index}-${name.toLowerCase().replace(/\s+/g, '-')}`;
    dnToIdMap.set(ou.dn, id);
    
    // Find parent by removing first RDN component
    const parentDn = dnParts.slice(1).join(',');
    const parentId = dnToIdMap.get(parentDn);
    
    // Determine type based on depth
    const depth = dnParts.length - baseDnParts.length;
    let type: OrgUnitType = 'team';
    if (depth === 0) type = 'organization';
    else if (depth === 1) type = 'division';
    else if (depth === 2) type = 'department';
    
    const description = ou.attributes[config.attributeMapping.groupDescription];
    
    units.push({
      id,
      externalId: ou.dn,
      name: name.toLowerCase().replace(/\s+/g, '-'),
      displayName: name,
      type,
      parentId,
      path: parentId ? [...(units.find(u => u.id === parentId)?.path || []), id] : [id],
      depth: parentId ? (units.find(u => u.id === parentId)?.depth || 0) + 1 : 0,
      syncSource: 'saml-generic', // or 'local' with LDAP indicator
      lastSyncedAt: new Date(),
      active: true,
      description: Array.isArray(description) ? description[0] : description,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
  });
  
  return units;
}

// ============================================================================
// Validation & Utilities
// ============================================================================

/**
 * Validate org unit hierarchy for circular references
 */
export function validateOrgUnitHierarchy(units: OrgUnit[]): string[] {
  const errors: string[] = [];
  const visited = new Set<string>();
  const visiting = new Set<string>();
  
  function detectCycle(unit: OrgUnit): boolean {
    if (visiting.has(unit.id)) {
      errors.push(`Circular reference detected: ${unit.displayName} (${unit.id})`);
      return true;
    }
    if (visited.has(unit.id)) return false;
    
    visiting.add(unit.id);
    
    if (unit.parentId) {
      const parent = units.find(u => u.id === unit.parentId);
      if (parent && detectCycle(parent)) {
        return true;
      }
    }
    
    visiting.delete(unit.id);
    visited.add(unit.id);
    return false;
  }
  
  units.forEach(unit => detectCycle(unit));
  
  return errors;
}

/**
 * Merge imported org units with existing ones
 */
export function mergeOrgUnits(
  existing: Map<string, OrgUnit>,
  imported: OrgUnit[],
  strategy: 'merge' | 'replace' | 'skip'
): { merged: OrgUnit[]; added: number; updated: number; skipped: number } {
  const merged: OrgUnit[] = [];
  let added = 0;
  let updated = 0;
  let skipped = 0;
  
  // Keep all existing units
  existing.forEach(unit => {
    const importedMatch = imported.find(i => 
      i.externalId === unit.externalId ||
      i.name === unit.name ||
      i.id === unit.id
    );
    
    if (importedMatch) {
      if (strategy === 'merge') {
        merged.push({
          ...unit,
          ...importedMatch,
          id: unit.id, // Keep original ID
          createdAt: unit.createdAt, // Keep original creation date
          updatedAt: new Date(),
        });
        updated++;
      } else if (strategy === 'replace') {
        merged.push({
          ...importedMatch,
          id: unit.id,
          createdAt: unit.createdAt,
          updatedAt: new Date(),
        });
        updated++;
      } else {
        merged.push(unit);
        skipped++;
      }
    } else {
      merged.push(unit);
    }
  });
  
  // Add new units that don't exist
  imported.forEach(unit => {
    const existingMatch = Array.from(existing.values()).find(e =>
      e.externalId === unit.externalId ||
      e.name === unit.name ||
      e.id === unit.id
    );
    
    if (!existingMatch) {
      merged.push(unit);
      added++;
    }
  });
  
  return { merged, added, updated, skipped };
}

// ============================================================================
// Export Utilities
// ============================================================================

/**
 * Export org units to CSV format
 */
export function exportOrgUnitsToCsv(units: OrgUnit[]): string {
  const headers = ['id', 'name', 'displayName', 'type', 'parentId', 'description', 'costCenter', 'location', 'active'];
  const rows = [headers.join(',')];
  
  units.forEach(unit => {
    const row = [
      unit.id,
      unit.name,
      `"${unit.displayName.replace(/"/g, '""')}"`,
      unit.type,
      unit.parentId || '',
      unit.description ? `"${unit.description.replace(/"/g, '""')}"` : '',
      unit.costCenter || '',
      unit.location || '',
      unit.active ? 'true' : 'false',
    ];
    rows.push(row.join(','));
  });
  
  return rows.join('\n');
}
